import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { mediaSrc } from "../lib/api";
import MediaCard from "../components/MediaCard";
import { useFeedCache, readFeedCache, restoreScroll } from "../hooks/useFeedCache";
import { Loader2, Hash } from "lucide-react";

export default function ConceptPage() {
  const { key } = useParams();
  const cacheKey = `concept:${key}`;
  const cached = readFeedCache(cacheKey);
  const [data, setData] = useState(cached?.data || null);
  const [loading, setLoading] = useState(!cached?.data);

  useEffect(() => {
    document.title = `${key} — KinkVault`;
    const c = readFeedCache(cacheKey);
    if (c?.data) { setData(c.data); setLoading(false); restoreScroll(c.scrollY); return; }
    setLoading(true);
    api.get(`/c/${key}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useFeedCache(cacheKey, data?.items || [], true, { data });

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#00E5FF]" size={30} /></div>;
  if (!data) return <div className="text-center py-32 text-[#52525B] font-mono text-sm" data-testid="concept-404">Concept not found.</div>;

  const hero = data.items[0];
  const heroSrc = hero ? mediaSrc(hero.thumb_url || hero.url) : null;

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-16 pb-16" data-testid="concept-page">
      {heroSrc ? (
        <div className="relative rounded-3xl overflow-hidden mb-8 h-52 sm:h-72 border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,1)]" data-testid="concept-hero">
          <img src={heroSrc} alt={data.concept.label} className="absolute inset-0 w-full h-full object-cover scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
          <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
            <span className="mb-2 w-fit text-[10px] font-mono uppercase tracking-widest text-[#00E5FF] bg-[#00E5FF]/15 border border-[#00E5FF]/30 px-2 py-0.5 rounded-full">{data.concept.kind}</span>
            <h1 className="font-heading text-4xl sm:text-6xl uppercase tracking-tighter text-white drop-shadow-lg">{data.concept.label}</h1>
            <p className="text-[#D4D4D8] text-sm mt-2 max-w-2xl">
              {data.count} item{data.count === 1 ? "" : "s"} · surfaced by the Semantic Index{data.concept.synonyms?.length ? ` · also: ${data.concept.synonyms.slice(0, 6).join(", ")}` : ""}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#00E5FF]/12 border border-[#00E5FF]/25"><Hash size={20} className="text-[#00E5FF]" /></span>
            <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tighter text-white">{data.concept.label}</h1>
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/25 px-2 py-0.5 rounded-full">{data.concept.kind}</span>
          </div>
          <p className="text-[#A1A1AA] text-sm mb-8">{data.count} items · understood by the Semantic Index{data.concept.synonyms?.length ? ` · also: ${data.concept.synonyms.slice(0, 6).join(", ")}` : ""}</p>
        </>
      )}
      {data.items.length ? (
        <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 gap-2 sm:gap-3" data-testid="concept-grid">
          {data.items.map((it, i) => <MediaCard key={it.id} item={it} index={i} />)}
        </div>
      ) : (
        <div className="border border-dashed border-[#1C1C1F] rounded-2xl py-20 text-center text-[#52525B] font-mono text-xs uppercase tracking-widest">Nothing indexed for this concept yet</div>
      )}
    </div>
  );
}
